import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/UI/Modal/ModalPrimitive'
import FbButton from 'components/FbUI/FbButton'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { useCreateBrand, useUpdateBrand } from 'services/products'
import type { Brand } from 'types/brands'
import { toast } from 'react-toastify'
import { SelectableImageTable } from './SelectableImageTable'
import { X } from 'lucide-react'

interface BrandModalProps {
  show: boolean
  onHide: () => void
  brand?: Brand | null
}

interface BrandFormData {
  name: string
}

/** Dialog modal for creating or editing a brand. Pass `brand` prop to edit, omit to create. */
export function BrandModal({ show, onHide, brand }: BrandModalProps) {
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null)
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)

  const createBrand = useCreateBrand()
  const updateBrand = useUpdateBrand()

  const isEditing = !!brand

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<BrandFormData>({
    defaultValues: { name: '' },
    mode: 'onChange',
  })

  useEffect(() => {
    if (brand) {
      reset({ name: brand.name })
      setSelectedImageId(brand.image_id ?? null)
      setSelectedImageUrl(brand.image_url ?? null)
    } else {
      reset({ name: '' })
      setSelectedImageId(null)
      setSelectedImageUrl(null)
    }
  }, [brand, reset])

  const onSubmit = async (formData: BrandFormData) => {
    const trimmedName = formData.name.trim()
    if (!trimmedName) return

    try {
      if (isEditing && brand) {
        await updateBrand.mutateAsync({
          id: brand.id,
          data: { name: trimmedName, image_id: selectedImageId },
        })
        toast.success('Brand updated successfully')
      } else {
        await createBrand.mutateAsync({
          data: { name: trimmedName, image_id: selectedImageId },
        })
        toast.success('Brand created successfully')
      }
      onHide()
    } catch (error) {
      console.error('Error saving brand:', error)
      toast.error(
        isEditing ? 'Failed to update brand' : 'Failed to create brand'
      )
    }
  }

  const isSubmitting = createBrand.isPending || updateBrand.isPending

  const handleSelectImage = useCallback(
    (imageId: number | null, imageUrl?: string) => {
      setSelectedImageId(imageId)
      setSelectedImageUrl(imageUrl ?? null)
    },
    []
  )

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="sm:max-w-[1050px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Brand' : 'Create Brand'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="pb-4 pt-2">
            <TextInput
              control={control}
              name="name"
              type="text"
              label="Brand Name"
              placeholder="Enter brand name"
              rules={{ required: 'Brand name is required' }}
            />

            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Brand Logo (optional)
              </label>
              {selectedImageId && selectedImageUrl && (
                <div className="mb-4 flex items-center gap-3">
                  <img
                    src={selectedImageUrl}
                    alt="Selected brand image"
                    className="h-12 w-12 rounded object-cover"
                  />
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    onClick={() => handleSelectImage(null)}
                  >
                    <X size={14} />
                    Remove
                  </button>
                </div>
              )}
              <p className="mb-2 text-xs text-gray-500">
                The selected image will be automatically assigned to this brand.
              </p>
              <SelectableImageTable
                selectedImageId={selectedImageId}
                onSelectImage={handleSelectImage}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 py-2">
            <FbButton
              variant="secondary"
              size="lg"
              className="flex-1"
              onClick={onHide}
              type="button"
              disabled={isSubmitting}
            >
              Cancel
            </FbButton>
            <FbButton
              size="lg"
              className="flex-1"
              type="submit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
            >
              {isEditing ? 'Save Changes' : 'Create Brand'}
            </FbButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
